import { r as registerInstance, c as createEvent, h, H as Host, g as getElement } from './index-61528ac5.js';
import { v as version } from './package-27c9b485.js';
import { d as checkEmptyOrType, a as checkType } from './index-f41b763f.js';
import { c as checkNonEmpty } from './check-non-empty-58bd6b17.js';

function debounce(callback, timeout = 200) {
    let id;
    return (...args) => {
        if (id)
            clearTimeout(id);
        id = setTimeout(callback, timeout, ...args);
    };
}

function getRoot(element) {
    const root = element.getRootNode();
    if (root instanceof Document || root instanceof ShadowRoot) {
        return root;
    }
    throw new Error('Attempting to access root node before the element is attached to the document or shadow tree.');
}

function isMotionReduced() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

const collapseDuration = 350;
const collapseEasing = 'ease';
const collapsedKeyframe = { height: '0', overflow: 'hidden' };
const animationOptions = {
    duration: collapseDuration,
    easing: collapseEasing,
    fill: 'forwards',
};
const collapse = (el) => {
    const expandedKeyframe = { height: window.getComputedStyle(el).height };
    return el.animate([expandedKeyframe, collapsedKeyframe], animationOptions);
};
const expand = (el) => {
    const expandedKeyframe = { height: `${el.scrollHeight}px`, offset: 1 };
    const finalKeyframe = { height: 'auto' };
    return el.animate([collapsedKeyframe, expandedKeyframe, finalKeyframe], animationOptions);
};

const postCollapsibleCss = ":host{display:block;overflow:hidden}";
const PostCollapsibleStyle0 = postCollapsibleCss;

const PostCollapsible = class {
    constructor(hostRef) {
        registerInstance(this, hostRef);
        this.postToggle = createEvent(this, "postToggle", 7);
        this.isLoaded = false;
        this.isOpen = true;
        this.collapsed = false;
    }
    collapsedChange() {
        checkEmptyOrType(this.collapsed, 'boolean', 'The `collapsed` property of the `post-collapsible` must be a boolean.');
        void this.toggle(!this.collapsed);
    }
    componentDidLoad() {
        this.collapsedChange();
        this.isLoaded = true;
        this.updateTriggers();
    }
    /**
     * Triggers the collapse programmatically.
     *
     * If there is a collapsing transition running already, it will be reversed.
     */
    async toggle(open = !this.isOpen) {
        if (open === this.isOpen)
            return open;
        this.isOpen = open;
        this.collapsed = !open;
        if (this.isLoaded)
            this.postToggle.emit(open);
        const animation = open ? expand(this.host) : collapse(this.host);
        if (!this.isLoaded || isMotionReduced())
            animation.finish();
        await animation.finished;
        animation.commitStyles();
        return open;
    }
    /**
     * Update all post-collapsible-trigger elements referring to the collapsible
     */
    updateTriggers() {
        const triggers = document.querySelectorAll(`post-collapsible-trigger[for="${this.host.id}"]`);
        triggers.forEach(trigger => trigger.update());
    }
    render() {
        return (h(Host, { key: '27c09f5612f7a2ef48c3ed97bfae29a8c9ea78ca', "data-version": version }, h("slot", { key: 'df30089afa0ff33d945dcb4c381ac08acb7cb08c' })));
    }
    get host() { return getElement(this); }
    static get watchers() { return {
        "collapsed": ["collapsedChange"]
    }; }
};
PostCollapsible.style = PostCollapsibleStyle0;

const PostCollapsibleTrigger = class {
    constructor(hostRef) {
        registerInstance(this, hostRef);
        this.observer = new MutationObserver(() => this.setTrigger());
        this.debouncedUpdate = debounce(() => {
            var _a;
            if (!this.trigger)
                return;
            // add the provided id to the aria-controls list
            const ariaControls = this.trigger.getAttribute('aria-controls');
            if (!(ariaControls === null || ariaControls === void 0 ? void 0 : ariaControls.includes(this.for))) {
                const newAriaControls = ariaControls ? `${ariaControls} ${this.for}` : this.for;
                this.trigger.setAttribute('aria-controls', newAriaControls);
            }
            // set the aria-expanded to `false` if the controlled collapsible is collapsed or undefined, set it to `true` otherwise
            const isCollapsed = (_a = this.collapsible) === null || _a === void 0 ? void 0 : _a.collapsed;
            const newAriaExpanded = isCollapsed !== undefined ? !isCollapsed : undefined;
            this.trigger.setAttribute('aria-expanded', `${newAriaExpanded}`);
        });
        this.for = undefined;
    }
    /**
     * Set the "aria-controls" and "aria-expanded" attributes on the trigger to match the state of the controlled post-collapsible
     */
    setAriaAttributes() {
        checkNonEmpty(this.for, 'The post-collapsible-trigger "for" prop is required.');
        checkType(this.for, 'string', 'The post-collapsible-trigger "for" prop should be a id.');
        void this.update();
    }
    /**
     * Initiate a mutation observer that updates the trigger whenever necessary
     */
    connectedCallback() {
        this.observer.observe(this.host, { childList: true, subtree: true });
    }
    /**
     * Attach a "postToggle" event listener to the root node
     * to update the trigger's "aria-expanded" attribute whenever the controlled post-collapsible is toggled
     */
    componentWillLoad() {
        this.root = getRoot(this.host);
        this.root.addEventListener('postToggle', (e) => {
            if (!this.trigger || !e.target.isEqualNode(this.collapsible))
                return;
            this.trigger.setAttribute('aria-expanded', `${e.detail}`);
        });
    }
    /**
     * Add the "data-version" to the host element and set the trigger
     */
    componentDidLoad() {
        this.host.setAttribute('data-version', version);
        this.setTrigger();
        if (!this.trigger)
            console.warn('The post-collapsible-trigger must contain a button.');
    }
    /**
     * Disconnect the mutation observer
     */
    disconnectedCallback() {
        this.observer.disconnect();
    }
    /**
     * Update the "aria-controls" and "aria-expanded" attributes on the trigger button
     */
    async update() {
        this.debouncedUpdate();
    }
    /**
     * Toggle the post-collapsible controlled by the trigger
     */
    async toggleCollapsible() {
        var _a;
        await ((_a = this.collapsible) === null || _a === void 0 ? void 0 : _a.toggle());
    }
    /**
     * Retrieve the post-collapsible controlled by the trigger
     */
    get collapsible() {
        const ref = this.root.getElementById(this.for);
        if (ref && ref.localName === 'post-collapsible') {
            return ref;
        }
        return null;
    }
    /**
     * Find the button and add the proper event listener and ARIA attributes to it
     */
    setTrigger() {
        const trigger = this.host.querySelector('button');
        if (!trigger || (this.trigger && trigger.isEqualNode(this.trigger)))
            return;
        this.trigger = trigger;
        this.trigger.addEventListener('click', () => this.toggleCollapsible());
        this.setAriaAttributes();
    }
    get host() { return getElement(this); }
    static get watchers() { return {
        "for": ["setAriaAttributes"]
    }; }
};

export { PostCollapsible as P, PostCollapsibleTrigger as a };
